import { faTimeline } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExternalLink } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import SectionCard from './SectionCard';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { GetCaseOverviewRes } from '@/api/types/queries';
import { Button } from '@/components/atoms/new-button';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Fact } from '@/types';

interface KeyFactsSectionProps {
  caseId: string | null;
  keyFacts: Fact[] | undefined;
  keyFactsIsLoading: boolean;
  overview: GetCaseOverviewRes | undefined;
  onCreateChronology: () => void;
}

export const KeyFactsSection = ({
  caseId,
  keyFacts,
  keyFactsIsLoading,
  overview,
  onCreateChronology,
}: KeyFactsSectionProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleViewKeyFacts = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', '0');
    searchParams.set('viewKeyFacts', 'true');

    trackCustomEvent(TrackingEvents.ViewKeyFactsFromSummary);

    navigate('/app/chronos/case-editor/data/facts?' + searchParams.toString());
  };
  return (
    <div className="lg:col-span-2">
      <SectionCard
        title="Key Facts"
        className="h-full"
        headerActions={
          <button
            onClick={handleViewKeyFacts}
            className="inline-flex items-center gap-1 text-sm text-blue-500 underline hover:text-blue-700"
          >
            View Key Facts <ExternalLink className="h-3 w-3" />
          </button>
        }
      >
        {keyFactsIsLoading ? (
          <div className="space-y-3">
            {/* Stats grid skeleton */}
            <div className="grid w-full grid-cols-2 gap-0 rounded-md border border-gray-100 bg-gray-50 p-2">
              <div className="flex items-center gap-1 border-r border-b border-gray-200 px-2 py-1">
                <Skeleton className="h-4 w-8 bg-gray-200" />
                <Skeleton className="h-3 w-16 bg-gray-200" />
              </div>
              <div className="flex items-center gap-1 border-b border-gray-200 px-2 py-1">
                <Skeleton className="h-4 w-8 bg-gray-200" />
                <Skeleton className="h-3 w-20 bg-gray-200" />
              </div>
              <div className="flex items-center gap-1 border-r border-gray-200 px-2 py-1">
                <Skeleton className="h-4 w-8 bg-gray-200" />
                <Skeleton className="h-3 w-16 bg-gray-200" />
              </div>
              <div className="flex items-center gap-1 px-2 py-1">
                <Skeleton className="h-4 w-8 bg-gray-200" />
                <Skeleton className="h-3 w-20 bg-gray-200" />
              </div>
            </div>
            {/* Button skeleton */}
            <div className="flex justify-between gap-2">
              <Skeleton className="h-9 w-full bg-gray-200" />
            </div>
          </div>
        ) : caseId && keyFacts && keyFacts.length > 0 ? (
          <>
            {/* Stats Row: 2x2 grid, with dividing lines */}
            <div className="mb-3 grid w-full grid-cols-2 gap-0 rounded-md border border-gray-100 bg-gray-50 text-xs text-gray-600">
              {overview?.keyFacts !== undefined && (
                <div className="flex items-center gap-1 border-r border-b border-gray-200 px-2 py-1">
                  <span className="font-semibold text-blue-600">{overview.keyFacts}</span>
                  <span>Key Facts</span>
                </div>
              )}
              {overview?.relevantFacts !== undefined && (
                <div className="flex items-center gap-1 border-b border-gray-200 px-2 py-1">
                  <span className="font-semibold text-blue-600">{overview.relevantFacts}</span>
                  <span>Relevant Facts</span>
                </div>
              )}
              {overview?.totalFacts !== undefined && (
                <div className="flex items-center gap-1 border-r border-gray-200 px-2 py-1">
                  <span className="font-semibold text-blue-600">{overview.totalFacts}</span>
                  <span>Total Facts</span>
                </div>
              )}
              {overview?.disputedFacts !== undefined && (
                <div className="flex items-center gap-1 px-2 py-1">
                  <span className="font-semibold text-blue-600">{overview.disputedFacts}</span>
                  <span>Disputed Facts</span>
                </div>
              )}
            </div>

            <div className="flex justify-between gap-2">
              <Button className="w-full text-xs" size="sm" variant="primary" onClick={onCreateChronology}>
                <FontAwesomeIcon icon={faTimeline} className="mr-2" />
                Create Chronology
              </Button>
            </div>
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">No key facts</div>
        )}
      </SectionCard>
    </div>
  );
};
